import { useEffect, useState, type FormEvent } from "react";
import { Shield, UserCog, WalletCards, Wrench, X } from "lucide-react";
import {
  createUserInputSchema,
  type Role,
  updateUserInputSchema,
  type UserView,
} from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

interface UserFormModalProps {
  user?: UserView;
  onClose: () => void;
  onSaved: (user: UserView) => void;
}

const roleOptions: Array<{ role: Role; label: string; description: string; icon: typeof Shield }> = [
  { role: "ADMIN", label: "Administrador", description: "Acesso integral ao sistema", icon: Shield },
  { role: "PRE_IMPLEMENTATION", label: "Pré-implantação", description: "Gestão de POCs e Trials", icon: Wrench },
  { role: "FINANCE", label: "Financeiro", description: "Consulta de Clientes", icon: WalletCards },
];

export function UserFormModal({ user, onClose, onSaved }: UserFormModalProps) {
  const editing = Boolean(user);
  const [name, setName] = useState(user?.name ?? "");
  const [prefix, setPrefix] = useState(user?.emailPrefix ?? "");
  const [roles, setRoles] = useState<Role[]>(user?.roles ?? ["PRE_IMPLEMENTATION"]);
  const [status, setStatus] = useState<"ACTIVE" | "DISABLED">(user?.status ?? "ACTIVE");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const toggleRole = (role: Role) => {
    if (role === "ADMIN") {
      setRoles(roles.includes("ADMIN") ? ["PRE_IMPLEMENTATION"] : ["ADMIN"]);
      return;
    }

    const withoutAdmin = roles.filter((item) => item !== "ADMIN");
    setRoles(
      withoutAdmin.includes(role)
        ? withoutAdmin.filter((item) => item !== role)
        : [...withoutAdmin, role],
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let saved: UserView;
      if (user) {
        const input = updateUserInputSchema.parse({ name, roles, status });
        saved = await api.updateUser(user.id, input);
      } else {
        const input = createUserInputSchema.parse({ name, prefix, roles });
        saved = await api.createUser(input);
      }
      onSaved(saved);
    } catch (requestError) {
      if (requestError instanceof ApiClientError) {
        setError(requestError.message);
      } else if (requestError instanceof Error) {
        setError(requestError.message);
      } else {
        setError("Não foi possível salvar o usuário.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="user-modal-title">
        <header className="modal-card__header">
          <div className="modal-card__title">
            <span className="modal-card__icon"><UserCog aria-hidden="true" /></span>
            <div><span className="eyebrow eyebrow--dark">Administração</span><h2 id="user-modal-title">{editing ? "Editar usuário" : "Novo usuário"}</h2></div>
          </div>
          <button className="icon-button" type="button" aria-label="Fechar" onClick={onClose}><X /></button>
        </header>

        <form className="modal-form" onSubmit={(event) => void submit(event)} noValidate>
          <label htmlFor="user-name">Nome completo</label>
          <input id="user-name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" maxLength={120} />

          <label htmlFor="user-prefix">Usuário</label>
          <div className={`suffix-input ${editing ? "suffix-input--disabled" : ""}`}>
            <input
              id="user-prefix"
              value={prefix}
              onChange={(event) => setPrefix(event.target.value.toLowerCase().replace("@", ""))}
              disabled={editing}
              autoCapitalize="none"
              maxLength={64}
            />
            <span>@ubisafe.com.br</span>
          </div>

          <fieldset className="role-fieldset">
            <legend>Perfis de acesso</legend>
            <div className="role-options">
              {roleOptions.map((option) => {
                const Icon = option.icon;
                const selected = roles.includes(option.role);
                return (
                  <label className={selected ? "role-option role-option--selected" : "role-option"} key={option.role}>
                    <input type="checkbox" checked={selected} onChange={() => toggleRole(option.role)} />
                    <Icon aria-hidden="true" />
                    <span><strong>{option.label}</strong><small>{option.description}</small></span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {editing && (
            <fieldset className="status-fieldset">
              <legend>Estado da conta</legend>
              <label className="toggle-row">
                <input type="checkbox" checked={status === "ACTIVE"} onChange={(event) => setStatus(event.target.checked ? "ACTIVE" : "DISABLED")} />
                <span className="toggle" aria-hidden="true" />
                <span><strong>{status === "ACTIVE" ? "Conta ativa" : "Conta desativada"}</strong><small>Contas desativadas não podem entrar no sistema.</small></span>
              </label>
            </fieldset>
          )}

          {!editing && <div className="form-note">Uma senha temporária será enviada ao e-mail corporativo e deverá ser alterada no primeiro acesso.</div>}
          {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}

          <footer className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose}>Cancelar</button>
            <button className="primary-button" type="submit" disabled={loading}>{loading ? "Salvando…" : "Salvar usuário"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
